import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { XCircle, ArrowRight, RefreshCw } from "lucide-react";

const NotionError = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string>("An unexpected error occurred.");
  
  useEffect(() => {
    // Get the error message from the URL query parameters
    const params = new URLSearchParams(location.search);
    const message = params.get("message");
    if (message) {
      setErrorMessage(decodeURIComponent(message));
    }
    
    // Scroll to top
    window.scrollTo(0, 0);
  }, [location]);

  const handleRetry = () => {
    navigate("/integrations");
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-6">
      <motion.div 
        className="max-w-md w-full mx-auto text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-8 flex justify-center">
          <div className="rounded-full bg-red-500/20 p-3 text-red-500">
            <XCircle className="h-8 w-8" />
          </div>
        </div>
        
        <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
          Connection Failed
        </h1>
        
        <div className="p-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-gray-300">
            {errorMessage}
          </p>
        </div>
        
        <p className="text-gray-400 mb-8">
          We couldn't connect your DocGen account to Notion. This might be due to permission issues or a temporary problem.
        </p>
        
        <div className="space-y-4">
          <Button 
            className="w-full bg-gradient-to-r from-primary to-blue-500 text-background hover:opacity-90 transition-all duration-300"
            onClick={handleRetry}
          >
            <span className="flex items-center">
              Try Again
              <RefreshCw className="ml-2 h-4 w-4" />
            </span>
          </Button>
          
          <Button 
            variant="ghost" 
            className="w-full text-gray-400 hover:text-white"
            onClick={() => navigate("/dashboard")}
          >
            <span className="flex items-center">
              Return to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </span>
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default NotionError; 